import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  BadgeCheck,
  Building2,
  Sparkles,
  UserRound,
  Save,
} from "lucide-react";

import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useToast } from "./ui/use-toast";
import { updateUser } from "src/services";

type BusinessLike = {
  id?: string;
  name?: string;
  email?: string;
  country?: string;
  type?: string;
  description?: string;
  status?: string;
  is_default?: boolean;
};

type UserLike = {
  id?: string;
  firstname?: string;
  lastname?: string;
  email?: string;
  trading_name?: string;
};

type BusinessTradeProfileProps = {
  business?: BusinessLike;
  user?: UserLike;
};

const BusinessTradeProfile = ({ business, user }: BusinessTradeProfileProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [tradeName, setTradeName] = useState(user?.trading_name || "");

  useEffect(() => {
    setTradeName(user?.trading_name || "");
  }, [user?.trading_name]);

  const tradeNameExists = useMemo(() => Boolean(String(user?.trading_name || "").trim()), [user?.trading_name]);

  const saveTradeProfile = useMutation({
    mutationFn: async () => {
      if (!user?.id) {
        throw new Error("Missing user id");
      }

      return updateUser(user.id, {
        trading_name: tradeName.trim(),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["userDetails"] });
      toast({
        description: "Trade profile updated successfully.",
      });
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save trade profile.",
      });
    },
  });

  const disableSave =
    !tradeName.trim() ||
    tradeName.trim() === String(user?.trading_name || "").trim() ||
    saveTradeProfile.isPending;

  return (
    <section className="space-y-5">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="max-w-3xl space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-rose-500">
            Trade profile
          </p>
          <h3 className="text-2xl font-bold text-slate-900">B2B trade identity and personal alias</h3>
          <p className="text-sm text-slate-500">
            Use this tab to review the selected business profile for B2B trading and create the
            trade name that appears across P2P and B2B flows.
          </p>
        </div>

        <span
          className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${
            tradeNameExists ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
          }`}
        >
          {tradeNameExists ? <BadgeCheck className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
          {tradeNameExists ? "Trade profile ready" : "Trade profile missing"}
        </span>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)]">
        <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-[0_20px_60px_rgba(15,23,42,0.04)]">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">
                Business trade profile
              </p>
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-rose-50 text-rose-600">
                  <Building2 className="h-5 w-5" />
                </div>
                <div>
                  <h4 className="text-xl font-bold text-slate-900">{business?.name || "Selected business"}</h4>
                  <p className="text-sm text-slate-500">
                    {business?.description || "B2B-ready business profile for trade and settlement."}
                  </p>
                </div>
              </div>
            </div>

            <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
              {business?.is_default ? "Default" : "Active"}
            </span>
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {[
              ["Email", business?.email || "N/A"],
              ["Country", business?.country || "N/A"],
              ["Type", business?.type || "N/A"],
              ["Status", business?.status || "Pending"],
            ].map(([label, value]) => (
              <div key={label} className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  {label}
                </p>
                <p className="mt-2 text-sm font-semibold text-slate-900">{value}</p>
              </div>
            ))}
          </div>

          <div className="mt-5 rounded-[24px] border border-rose-100 bg-rose-50/70 p-4">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="space-y-1">
                <p className="text-sm font-semibold text-slate-900">Built for B2B trading</p>
                <p className="text-sm text-slate-500">
                  This business profile is used in B2B trade flows, settlement, and transaction history.
                </p>
              </div>
              <Button
                type="button"
                onClick={() => navigate("/home/trading")}
                className="inline-flex items-center gap-2 rounded-full bg-rose-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-rose-700"
              >
                Open B2B Trade
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-[0_20px_60px_rgba(15,23,42,0.04)]">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-100 text-slate-600">
              <UserRound className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">
                Personal trade profile
              </p>
              <h4 className="mt-1 text-lg font-bold text-slate-900">
                {tradeNameExists ? "Your trade name is live" : "Create a trade name"}
              </h4>
              <p className="mt-1 text-sm text-slate-500">
                This name appears when you create P2P and B2B trade requests.
              </p>
            </div>
          </div>

          <div className="mt-5 space-y-4">
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Trade name
              </label>
              <Input
                value={tradeName}
                onChange={(event) => setTradeName(event.target.value)}
                placeholder="e.g. Blue Corner"
              />
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                Current value
              </p>
              <p className="mt-2 text-sm font-semibold text-slate-900">
                {user?.trading_name || "No trade name has been created yet."}
              </p>
              <p className="mt-1 text-sm text-slate-500">
                If this is empty, use the field above to create your trade profile.
              </p>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-slate-500">
                Save a trade name once, then reuse it in every trade flow.
              </p>
              <Button
                type="button"
                onClick={() => saveTradeProfile.mutate()}
                disabled={disableSave}
                className="inline-flex items-center gap-2 rounded-full bg-rose-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
              >
                <Save className="h-4 w-4" />
                {saveTradeProfile.isPending ? "Saving..." : tradeNameExists ? "Update trade name" : "Create trade profile"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BusinessTradeProfile;
